import MUILink from '@mui/material/Link';

import { Link } from '../Link';
import { Icon } from '../Icon';
import { Box } from '../Box';
import { FilePreviewWithZoom } from '../FilePreviewWithZoom';
import { Dialog } from '../Dialog';
import { useIsDesktop, useIsTablet } from '../hooks';
import {
  DocFileImage,
  ExcelFileImage,
  CsvFileImage,
  OdtFileImage,
  OdsFileImage,
  P7mFileImage,
  XmlFileImage,
  UnknownFileImage,
} from '../graphics';

import {
  PreviewImage,
  PreviewPdf,
  DownloadableFileIcon,
  PreviewSnackbar,
} from './Previews';
import { useStyles } from './FilePreviewWrapper.styles';
import { FilePreviewContentProps } from './FilePreview.types';

/**
 * Компонент для отображения контента превью файла в зависимости от типа файла.
 *
 * @internal
 * @param shape - Форма отображения файла (квадратная или автоматическая).
 * @param onClickPrint - Функция для печати файла.
 * @param onClickDownload - Функция для скачивания файла.
 * @param onLoadSuccess - Функция обратного вызова при успешной загрузке файла.
 * @param withCollapsableFooter - Флаг, указывающий, имеет ли файл сворачиваемый нижний колонтитул.
 * @param view - Вид отображения файла.
 * @param showSnackbarOnDesktop - Флаг, указывающий, следует ли показывать Snackbar на настольных устройствах.
 * @param snackbarMessage - Сообщение для Snackbar.
 * @param snackbarAction - Текст действия для Snackbar.
 * @param linkText - Текст ссылки для просмотра файла.
 * @param withFlex - Флаг, указывающий, следует ли использовать Flex для центрирования изображения.
 * @param centerImage - Флаг, указывающий, следует ли центрировать изображение.
 * @param withZoom - Флаг, указывающий, раскрывается ли файл для предпросмотра c масштабированием.
 * @param blob - Расширенный объект File с дополнительным полем path (путь к файлу).
 * @param blobType - Тип файла для определения способа отображения.
 * @param objectUrl - URL созданный через URL.createObjectURL() для предпросмотра содержимого файла.
 * @param onLoadFull - Функция для загрузки полной версии файла.
 * @param handleOpen - Обработчик открытия превью (обычно в модальном окне/полноэкранном режиме).
 * @param handleClose - Обработчик закрытия превью.
 * @param clickable - Флаг, указывающий, должен ли компонент реагировать на клики.
 * @param open - Контролируемое состояние, указывающее открыто ли превью.
 * @param alternativeImageComponent - Если true, то использует альтернативный компонент для загрузки изображения.
 * @param height - Высота изображения.
 * @param maxHeight - Максимальная высота изображения.
 * @param maxWidth - Максимальная ширина изображения.
 * @param width - Ширина изображения.
 * @returns  JSX-элемент, представляющий контент превью файла.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link FilePreviewContentProps}
 */
export const FilePreviewContent = ({
  shape = 'auto',
  onClickPrint,
  onClickDownload,
  onLoadSuccess,
  withCollapsableFooter,
  view = 'file',
  showSnackbarOnDesktop = false,
  snackbarMessage,
  snackbarAction,
  linkText,
  withFlex = false,
  centerImage = false,
  withZoom = false,
  height,
  width,
  maxHeight,
  maxWidth,
  blob,
  blobType,
  objectUrl,
  onLoadFull,
  handleOpen,
  handleClose,
  clickable,
  open,
  alternativeImageComponent = false,
}: FilePreviewContentProps) => {
  const { classes } = useStyles({ mb: {}, mt: {} });
  const isDesktop = useIsDesktop();
  const isTablet = useIsTablet();

  if (blobType === 'application/pdf') {
    const links =
      view === 'fileLarge' ? (
        <Box
          p="24"
          py="16"
          bgcolor="bg.greyscale.primary"
          radius="l"
          maxHeight={400}
          overflow="hidden"
          position="relative"
          className="aqa_file_preview_pdf_wrapper"
        >
          <PreviewPdf
            url={objectUrl}
            shape={shape}
            onClickDownload={onClickDownload}
            onLoadSuccess={onLoadSuccess}
          />
          <MUILink onClick={handleOpen}>
            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              bgcolor="bg.dark.primary"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p="12"
            >
              <Box color="bg.light.primary">{linkText}</Box>
              <Box color="bg.light.primary">
                <Icon name="chevron_right_outline" size="20" />
              </Box>
            </Box>
          </MUILink>
        </Box>
      ) : (
        <Link onClick={handleOpen} stroke={false} size="small">
          {linkText || 'Просмотр'}
        </Link>
      );

    return clickable ? (
      <>
        {view === 'file' ? (
          <Box
            className={`${classes.documentCursor} aqa_file_preview_pdf_wrapper`}
          >
            <PreviewPdf
              url={objectUrl}
              shape={shape}
              onClickPrint={onClickPrint}
              onOpen={handleOpen}
              onClickDownload={onClickDownload}
              withCollapsableFooter={withCollapsableFooter}
              onLoadSuccess={onLoadSuccess}
            />
          </Box>
        ) : (
          links
        )}
        {isDesktop && withZoom ? (
          <FilePreviewWithZoom
            onClose={handleClose}
            open={open}
            url={objectUrl}
            onLoadSuccess={onLoadSuccess}
            withBottomOffset={showSnackbarOnDesktop}
            alternativeImageComponent={alternativeImageComponent}
          />
        ) : (
          <Dialog
            scroll="body"
            onClose={handleClose}
            open={open}
            fullScreen={!isDesktop}
            className="aqa_file_preview_dialog"
            classes={{
              paper: classes.dialogPaper,
            }}
            withoutRadius={!isDesktop}
          >
            <PreviewPdf
              url={objectUrl}
              shape="auto"
              onLoadSuccess={onLoadSuccess}
              onClickDownload={!isDesktop ? onClickDownload : undefined}
            />
          </Dialog>
        )}
        {(showSnackbarOnDesktop || !isDesktop) && (
          <PreviewSnackbar
            open={open}
            onClick={handleClose}
            message={snackbarMessage}
            action={snackbarAction}
          />
        )}
      </>
    ) : (
      <Box>
        <PreviewPdf
          url={objectUrl}
          shape={shape}
          onClickPrint={onClickPrint}
          onClickDownload={onClickDownload}
          withCollapsableFooter={withCollapsableFooter}
          onLoadSuccess={onLoadSuccess}
          {...(withZoom && { onExpand: handleOpen })}
        />
        {withZoom && isDesktop && (
          <FilePreviewWithZoom
            onClose={handleClose}
            open={open}
            url={objectUrl}
            onLoadSuccess={onLoadSuccess}
            withBottomOffset={showSnackbarOnDesktop}
          />
        )}
      </Box>
    );
  }

  if (blobType?.startsWith('image/')) {
    const renderImage = () => (
      <>
        <PreviewImage
          url={objectUrl}
          shape={shape}
          onLoadFull={onLoadFull}
          clickable={!!clickable}
          onClickDownload={onClickDownload}
          withCollapsableFooter={withCollapsableFooter}
          centerImage={centerImage}
          onLoadSuccess={onLoadSuccess}
          height={height}
          width={width}
          maxHeight={maxHeight}
          maxWidth={maxWidth}
          {...(withZoom && !clickable && { onExpand: handleOpen })}
        />
        {withZoom && (
          <FilePreviewWithZoom
            onClose={handleClose}
            open={open}
            url={objectUrl}
            onLoadFull={onLoadFull}
            onLoadSuccess={onLoadSuccess}
            withBottomOffset={showSnackbarOnDesktop}
            fileType="image"
          />
        )}
      </>
    );

    return withFlex ? (
      <Box
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {renderImage()}
      </Box>
    ) : (
      renderImage()
    );
  }

  if (blobType === 'text/xml' || blobType === 'application/xml') {
    return (
      <DownloadableFileIcon
        shape={shape}
        onClickDownload={onClickDownload}
        withCollapsableFooter={withCollapsableFooter}
      >
        <XmlFileImage width={isTablet ? 56 : 36} height={isTablet ? 56 : 36} />
      </DownloadableFileIcon>
    );
  }

  if (blobType === 'application/vnd.oasis.opendocument.text') {
    return (
      <DownloadableFileIcon
        shape={shape}
        onClickDownload={onClickDownload}
        withCollapsableFooter={withCollapsableFooter}
      >
        <OdtFileImage width={isTablet ? 56 : 36} height={isTablet ? 56 : 36} />
      </DownloadableFileIcon>
    );
  }

  if (blobType === 'application/vnd.oasis.opendocument.spreadsheet') {
    return (
      <DownloadableFileIcon
        shape={shape}
        onClickDownload={onClickDownload}
        withCollapsableFooter={withCollapsableFooter}
      >
        <OdsFileImage width={isTablet ? 56 : 36} height={isTablet ? 56 : 36} />
      </DownloadableFileIcon>
    );
  }

  if (blobType === 'application/pkcs7-mime' || blob?.path?.endsWith('.p7m')) {
    return (
      <DownloadableFileIcon
        shape={shape}
        onClickDownload={onClickDownload}
        withCollapsableFooter={withCollapsableFooter}
      >
        <P7mFileImage width={isTablet ? 56 : 36} height={isTablet ? 56 : 36} />
      </DownloadableFileIcon>
    );
  }

  if (blobType === 'text/csv' || blobType === 'application/csv') {
    return (
      <DownloadableFileIcon
        shape={shape}
        onClickDownload={onClickDownload}
        withCollapsableFooter={withCollapsableFooter}
      >
        <CsvFileImage width={isTablet ? 56 : 36} height={isTablet ? 56 : 36} />
      </DownloadableFileIcon>
    );
  }

  if (
    blobType === 'application/msword' ||
    blobType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return (
      <DownloadableFileIcon
        shape={shape}
        onClickDownload={onClickDownload}
        withCollapsableFooter={withCollapsableFooter}
      >
        <DocFileImage width={isTablet ? 56 : 36} height={isTablet ? 56 : 36} />
      </DownloadableFileIcon>
    );
  }

  if (
    blobType === 'application/pgp-signature' ||
    blob?.path?.endsWith('.sig')
  ) {
    return (
      <Box>
        <Box
          px="32"
          py="48"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon
            name="vkhrtek_sig_file"
            color="original.brand.primary"
            size="48"
          />
        </Box>
      </Box>
    );
  }

  if (
    blobType === 'application/vnd.ms-excel' ||
    blobType ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return (
      <DownloadableFileIcon
        shape={shape}
        onClickDownload={onClickDownload}
        withCollapsableFooter={withCollapsableFooter}
      >
        <ExcelFileImage
          width={isTablet ? 56 : 36}
          height={isTablet ? 56 : 36}
        />
      </DownloadableFileIcon>
    );
  }

  return (
    <DownloadableFileIcon
      shape={shape}
      onClickDownload={onClickDownload}
      withCollapsableFooter={withCollapsableFooter}
    >
      <UnknownFileImage
        width={isTablet ? 56 : 36}
        height={isTablet ? 56 : 36}
      />
    </DownloadableFileIcon>
  );
};
